import { useState, useEffect, useCallback, useRef } from 'react';
import { getActiveStrategy } from '../api/strategy';
import type { ActiveStrategyDto } from '../types/strategy';

const CACHE_TTL_MS = 30_000; // 30 seconds

interface CacheEntry {
  strategy: ActiveStrategyDto | null;
  timestamp: number;
}

// Module-level cache shared across all hook instances
const cache: Record<string, CacheEntry> = {};

/**
 * Invalidates the cached active-strategy entry for one province. Called by
 * `useStrategyTemplates`'s write methods after a mutation that can change what
 * `GET .../strategies/active` subsequently returns — the writer (Strategy.tsx, via
 * useStrategyTemplates) and the readers (Buildings/Training/Science/RealmHub, via this hook) are
 * different pages/hook instances, so there's no same-hook refresh() to call. The next mount of
 * any useStrategyGuidance instance for this worldId/provinceId does a real fetch instead of
 * serving a stale cache hit.
 */
export function invalidateStrategyGuidance(worldId: string, provinceId: string): void {
  delete cache[`${worldId}:${provinceId}`];
}

/** Test-only: resets the shared cache between test files/cases, mirroring
 *  `_resetDiplomacyCache()` / `_resetKingdomRelationshipsCache()` / `_resetDragonStatusCache()`. */
export function _resetStrategyGuidanceCache(): void {
  for (const key of Object.keys(cache)) delete cache[key];
}

interface UseStrategyGuidanceResult {
  activeStrategy: ActiveStrategyDto | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useStrategyGuidance(worldId: string | null, provinceId: string | null): UseStrategyGuidanceResult {
  const [activeStrategy, setActiveStrategy] = useState<ActiveStrategyDto | null>(null);
  // SF-172: initializes `true` (was `false`) — the previous default made the very first render
  // indistinguishable from "confirmed: no active strategy" (isLoading: false, activeStrategy:
  // null), before fetchStrategy()'s effect has even run. Every early-return path below must now
  // explicitly flip it back to `false`, or a consumer (e.g. RealmHub's Strategy card) is stuck
  // showing "Loading…" forever.
  const [isLoading, setIsLoading] = useState(true);
  const mounted = useRef(true);

  const cacheKey = `${worldId}:${provinceId}`;

  const fetchStrategy = useCallback(async (skipCache = false) => {
    if (!worldId || !provinceId) {
      setIsLoading(false);
      return;
    }

    // Check cache
    if (!skipCache) {
      const cached = cache[cacheKey];
      if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        setActiveStrategy(cached.strategy);
        setIsLoading(false);
        return;
      }
    }

    setIsLoading(true);
    try {
      const strategy = await getActiveStrategy(worldId, provinceId);
      cache[cacheKey] = { strategy, timestamp: Date.now() };
      if (mounted.current) {
        setActiveStrategy(strategy);
      }
    } catch {
      // Non-blocking: page renders without guidance on error
      if (mounted.current) {
        setActiveStrategy(null);
      }
    } finally {
      if (mounted.current) {
        setIsLoading(false);
      }
    }
  }, [worldId, provinceId, cacheKey]);

  useEffect(() => {
    mounted.current = true;
    fetchStrategy();
    return () => { mounted.current = false; };
  }, [fetchStrategy]);

  const refresh = useCallback(async () => {
    await fetchStrategy(true);
  }, [fetchStrategy]);

  return { activeStrategy, isLoading, refresh };
}
